import React, { act } from 'react';
import { useLockedOrderPrompt } from './lockedOrder';
import { createPortal } from 'react-dom';
import { RefreshCwIcon, Wrench, FileText, Sparkles, Database, Globe, FolderOpen, Car, Package, BookOpen, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Message, ClarificationOption, ClarificationQuestion, StructuredAnswer, ClarificationHint, RequestLine, ClarificationChatTurn } from './types';
import chatService from '../../../services/ChatService';
import { buildPartEditMarker, removeSupersededPartRows, PartEdit } from '../../../utils/partEdits';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';
import StreamingIndicator from './StreamingIndicator';
import StructuredQuestionForm, { PreviousClarificationStep } from './StructuredQuestionForm';
import SkippedQuestionCard, { PostponedOpenMode } from './SkippedQuestionCard';
import SuggestedActions from './SuggestedActions';
import MediaEmbeds, { stripInlineMediaTags, stripEmbeddableUrlLines } from './MediaEmbeds';
import MessageFeedback from './MessageFeedback';
import ProcessingSteps from './ProcessingSteps';
import SourceBadge from './SourceBadge';
import AnnotatedImagesBlock from './AnnotatedImagesBlock';
import MultiPartResultView from './MultiPartResultView';
import { RefinePopup, RefineItem } from './MultiPartResultView';
import DataSourceSuggestions from './DataSourceSuggestions';
import ExtSearchResultView from './ExtSearchResultView';
import { useOrder } from '../../../contexts/OrderContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';
import MessageDebugPanel from './MessageDebugPanel';
import VisionDebugInline from './VisionDebugInline';
import TurnClockChip from './TurnClockChip';
import VisionPhotoDetails from './VisionPhotoDetails';
import { getVersionedPublicUrl } from '../../../utils/versionUtils';
import { useHtmlTypingEffect } from '../../../hooks/useHtmlTypingEffect';
import SyncAgentResultView, { isSyncAgentResponse, SyncAgentResponse, isSyncAgentResponseShouldDisplay } from './SyncAgentResultView';
import ImageZoomModal from '../../common/ImageZoomModal';
import { SummaryItem, SummaryPartItem, buildCartNameList, isInCartByName } from './PartsTrackerPanel';
import { buildPartDisplayName, buildPartDisplayLabel, dedupeLabelQualifiers, splitPartAndRef, isPartNumberQuery } from '../../../utils/partLabel';
import { trackerItem } from '../../../utils/requestLines';

// Re-exported for backward compatibility — the type now lives with the panel.
export type { SummaryItem } from './PartsTrackerPanel';

/** Hidden sync-agent payloads should disappear only after their message has
 * finished. While streaming, the same bubble owns the user's processing
 * indicator and must remain mounted between the vision and validation frames. */
export function shouldHideSyncAgentMessage(
  syncAgent: SyncAgentResponse | undefined,
  isStreaming: boolean,
): boolean {
  return !isStreaming && !!syncAgent && !isSyncAgentResponseShouldDisplay(syncAgent);
}

/** Convert a gs:// URI to a public HTTPS URL. Passthrough for anything else. */
const toHttpUrl = (src: string): string => {
  if (src && src.startsWith('gs://')) {
    const withoutScheme = src.slice('gs://'.length);
    const slashIdx = withoutScheme.indexOf('/');
    if (slashIdx !== -1) {
      const bucket = withoutScheme.slice(0, slashIdx);
      const object = withoutScheme.slice(slashIdx + 1);
      return `https://storage.googleapis.com/${bucket}/${object}`;
    }
  }
  return src;
};

/** Payload lifted to the page when a validation response carries a parts list. */
export interface ValidationPartsPayload {
  messageId: string;
  items: SummaryItem[];
  serviceTitle?: string;
}

interface BotMessageProps {
  message: Message;
  isLastMessage: boolean;
  isLoading: boolean;
  /** Viewer may read but not act (order owned by someone else, shared view,
   *  closed order): the clarification form and follow-up pills are locked. */
  readOnly?: boolean;
  addingToCartItems: Set<string>;
  onRetry: (message: Message) => void;
  onFeedback: (messageId: string, helpful: boolean) => void;
  onSearchPart: (searchText: string) => void;
  /** Called to send a message programmatically without showing it in the chat input */
  onSendSilent?: (text: string) => void;
  /** Send a visible user message to the main chat (a new part search). */
  onSendMessage?: (text: string) => void;
  /** Earlier questions of the same narrowing, folded into this card. */
  previousClarifications?: PreviousClarificationStep[];
  /** This answered question waits for the next one (turn running). */
  waitingNextQuestion?: boolean;
  /**
   * Called when the user submits an in-bubble structured clarification form.
   * Receives BOTH a human-readable summary (shown in chat history) and the
   * structured_answer payload threaded into the request body.
   */
  onSubmitClarification?: (message: string, structuredAnswer: StructuredAnswer) => void;
  onAddToCart: (part: any, structuredData: any) => void;
  formatTime: (date: Date) => string;
  isValidImageData: (imageData: string | null) => boolean;
  onOrderUpdate?: () => void;
  orderUpdateTrigger?: number;
  cartItems?: any[];
  onSummaryItems?: (items: string[]) => void;
  /** When true, hides the StreamingIndicator steps panel while the bot is streaming */
  hideStreamingSteps?: boolean;
  /**
   * Annotated images collected from earlier (possibly hidden) messages.
   * When provided, these are shown inside the validation summary card instead of
   * (or in addition to) any images that arrived with this specific message.
   */
  persistedAnnotatedImages?: string[];
  /**
   * Map of OE part numbers (lowercased) → anchor element id already shown in earlier
   * messages in the same session. Passed through to MultiPartResultView to show a
   * "brought before" notice with a scroll link instead of suppressing duplicates.
   */
  seenPartNumbers?: Map<string, string>;
  /** Parts found in EARLIER bot messages — passed to MultiPartResultView so the
   *  results table can show a collapsed, grayed-out "full list" (history). */
  priorParts?: any[];
  /** Any message in the conversation carries a real request-line ledger. */
  sessionHasLedger?: boolean;
  /**
   * When provided, the floating PartsTrackerPanel owns the parts-list rendering:
   * validation parts are lifted through this callback and the in-bubble parts
   * card is suppressed (only assessment text + annotated images remain in the
   * bubble). When absent (guest/portal pages), the legacy in-bubble card renders.
   */
  onValidationParts?: (payload: ValidationPartsPayload) => void;
  /** Cumulative session validation parts (the tracker rows). Passed to
   *  MultiPartResultView so each card header matches its tracker row label. */
  validationItems?: SummaryItem[];
  /** True only for the most recent bot message that produced parts_results, so
   *  older results tables stay frozen when a later search runs (tracker still updates). */
  isLatestResults?: boolean;
  /** True while the deferred complementary round is still expected to deliver
   *  suggestions for this message — reserves the "You might also need" slot. */
  complementaryPending?: boolean;
  /** Opens the cart / order side panel — enables the "review & submit" suggested
   *  action chip on completed search turns (guest & portal pages). */
  onOpenCartPanel?: () => void;
  /**
   * When false, suggested-action chips that send free-form conversational text
   * ("yes, go ahead") are hidden — the guest workflow treats any message as a
   * part description, so only explicitly part-shaped / question-shaped
   * suggestions are safe there. Defaults to true (authenticated chat).
   */
  conversationalFollowups?: boolean;
}

/** Parse a single `<li>` element from the model's parts-list into a SummaryItem. */
function parsePartListItem(liHtml: string): SummaryItem | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(liHtml, 'text/html');
  const li = doc.querySelector('li');
  if (!li) return null;

  const classList = li.className;
  const isRemoved = /\bremoved\b/.test(classList) || /removed-item/.test(classList);
  const isEdited  = /edited-item/.test(classList);

  // Extract tooltip warning text before stripping markup.
  const tooltipPopup = li.querySelector('.tooltip-popup');
  const warning = tooltipPopup?.textContent?.trim() || undefined;

  // For edited items: old value is inside <span class="old-value">
  let oldName: string | undefined;
  if (isEdited) {
    oldName = li.querySelector('.old-value')?.textContent?.trim() || undefined;
    li.querySelectorAll('.old-value').forEach(el => el.remove());
  }

  // Strip all tooltip markup so only the part name remains.
  li.querySelectorAll('.tooltip-wrapper, .old-value').forEach(el => el.remove());
  const name = li.textContent?.trim() ?? '';

  if (!name || name === '...') return null;

  let status: SummaryItem['status'] = 'normal';
  if (isRemoved)       status = 'removed';
  else if (isEdited)   status = 'edited';
  else if (warning)    status = 'warning';

  return { name, status, warning, oldName };
}

/** Shape of the structured DataAgentResponse coming from the backend. */
interface DataAgentResponse {
  answer_html: string;
  source: 'grounding' | 'web_search' | 'mixed';
  vehicle?: string;
}

/** Return true if a parsed JSON object looks like a DataAgentResponse. */
function isDataAgentResponse(obj: any): obj is DataAgentResponse {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.answer_html === 'string' &&
    typeof obj.source === 'string' &&
    !('text_message' in obj)
  );
}

/** Shape of the structured ValidationAgentResponse coming from the backend. */
interface ValidationAgentResponse {
  is_greeting?: boolean;
  assessment_text?: string;
  parts?: Array<{
    /** @deprecated use original_name */
    name?: string;
    original_name?: string;
    corrected_name?: string;
    part_parameters?: string[];
    /** Display-only label fully in the client language (backend-translated). */
    display_label?: string;
    status: 'new' | 'previous' | 'removed' | 'edited' | 'not_in_oe';
    old_name?: string;
    warnings?: Array<{ dimension: string }>;
  }>;
  service_title?: string;
  execute_search?: boolean;
  session_part_uuids?: string[];
  context_tags?: string[];
  hasError?: boolean;
}

/** Return true if a parsed JSON object looks like a ValidationAgentResponse. */
function isValidationResponse(obj: any): obj is ValidationAgentResponse {
  return obj && typeof obj === 'object' && Array.isArray(obj.parts) && !('text_message' in obj);
}

/** A catalog section label: "86-877 – BODY SIDE MOULDING", "81-831 - MOLDURA". */
const SECTION_CODE_LABEL = /^\s*\d{2,3}\s*-\s*\d{2,4}\s*[–—-]/;

/** Normalize a raw clarification_questions array from the backend envelope into
 *  the UI shape, keeping only id+label+description and stripping the backend-only
 *  per-option load payload (`options[].load` / legacy `options[].parts`). */
export function normalizeClarificationQuestions(raw: any): ClarificationQuestion[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const questions: ClarificationQuestion[] = [];
  for (const q of raw) {
    if (!q || typeof q !== 'object') continue;
    const type = q.question_type;
    if (type !== 'single_select' && type !== 'multi_select' && type !== 'free_text') continue;
    const options: ClarificationOption[] = Array.isArray(q.options)
      ? q.options
          .filter((o: any) => o && typeof o === 'object')
          // Keep id + label + description, plus the read-only `load` refs (catalog
          // pointers for the "view diagram" feature). NEVER keep the legacy
          // `parts` array, and `load` is never echoed back in the answer.
          .map((o: any) => ({
            id: String(o.id),
            label: String(o.label ?? ''),
            description: o.description ?? null,
            // Score + side/position details ride along so the Hint request can
            // tell the model what the catalog knows about each candidate.
            probability_score: typeof o.probability_score === 'number' ? o.probability_score : null,
            extra_details: o.extra_details && typeof o.extra_details === 'object' ? o.extra_details : null,
            candidate_option_ids: Array.isArray(o.candidate_option_ids) ? o.candidate_option_ids.map((x: any) => String(x)) : undefined,
            candidate_count: typeof o.candidate_count === 'number' ? o.candidate_count : null,
            multi: o.multi === true,
            load: Array.isArray(o.load)
              ? o.load
                  .filter((r: any) => r && typeof r === 'object' && r.group_id && r.section_id)
                  .map((r: any) => ({
                    group_id: String(r.group_id),
                    section_id: String(r.section_id),
                    partId: String(r.partId ?? ''),
                    callout: r.callout != null ? String(r.callout) : null,
                    part_name: r.part_name ?? null,
                    part_number: r.part_number != null ? String(r.part_number) : null,
                  }))
              : [],
          }))
      : [];
    // Every question renders as the same card. A select question whose
    // options carry no catalog refs ("Which filter — oil, air or fuel?") is
    // answered from its option list; dropping it left the bubble showing only
    // the question's intro text with nothing to answer (live, 2026-09-26).
    // Still hidden: choices that are nothing but raw catalog section codes
    // ("86-877 – BODY SIDE MOULDING") with no diagram behind them — a customer
    // cannot answer those.
    if (type !== 'free_text' && options.length === 0) continue;
    const hasRefs = options.some(o => (o.load || []).some(r => !!r.group_id && !!r.section_id));
    const bareSectionCodes = options.length > 0 && options.every(o => SECTION_CODE_LABEL.test(o.label));
    if (type !== 'free_text' && q.question_kind !== 'expert_narrowing' && !hasRefs && bareSectionCodes) continue;
    questions.push({
      id: String(q.id),
      part_name: String(q.part_name ?? ''),
      part_label: q.part_label ? dedupeLabelQualifiers(String(q.part_label)) : null,
      part_parameters: Array.isArray(q.part_parameters) ? q.part_parameters.map((x: any) => String(x)) : null,
      // Below-threshold scorer candidates — display-only estimate badges for
      // the diagram's "other parts" rows.
      scored_parts: Array.isArray(q.scored_parts)
        ? q.scored_parts
            .filter((sp: any) => sp && typeof sp === 'object' && sp.partId && typeof sp.probability_score === 'number')
            .map((sp: any) => ({
              partId: String(sp.partId),
              probability_score: sp.probability_score,
              group_id: sp.group_id != null ? String(sp.group_id) : null,
              section_id: sp.section_id != null ? String(sp.section_id) : null,
              callout: sp.callout != null ? String(sp.callout) : null,
              part_number: sp.part_number != null ? String(sp.part_number) : null,
            }))
        : null,
      question_type: type,
      text: String(q.text ?? ''),
      options,
      candidate_total: typeof q.candidate_total === 'number' ? q.candidate_total : null,
      parts_explained: q.parts_explained === true,
      open_diagram: q.open_diagram === true,
      diagram_reason: q.diagram_reason ? String(q.diagram_reason) : null,
      // The candidates behind a narrowing question ("Front or rear?"): its EPC
      // diagram opens in place, and a part clicked there answers it.
      expert_fallback: q.question_kind === 'expert_narrowing' && q.expert_fallback && typeof q.expert_fallback === 'object'
        ? (normalizeClarificationQuestions([q.expert_fallback])?.[0] ?? null)
        : null,
      narrowing_axis: q.narrowing_axis ? String(q.narrowing_axis) : null,
      narrowing_trail: Array.isArray(q.narrowing_trail)
        ? q.narrowing_trail
            .filter((s: any) => s && typeof s === 'object' && s.label && typeof s.from === 'number' && typeof s.to === 'number')
            .map((s: any) => ({
              label: String(s.label), from: s.from, to: s.to,
              axis: s.axis ? String(s.axis) : null, value: s.value ? String(s.value) : null,
              carried_from: s.carried_from ? String(s.carried_from) : null,
            }))
        : null,
      // Backend fit check: the shortlist holds only neighbours of the requested part.
      candidate_fit: q.candidate_fit && typeof q.candidate_fit === 'object'
        && (q.candidate_fit.status === 'related' || q.candidate_fit.status === 'unrelated') && q.candidate_fit.note
        ? { status: q.candidate_fit.status, note: String(q.candidate_fit.note) }
        : null,
      plan_step: q.plan_step && typeof q.plan_step === 'object'
        && Number.isFinite(Number(q.plan_step.current)) && Number.isFinite(Number(q.plan_step.total))
        ? { current: Number(q.plan_step.current), total: Number(q.plan_step.total) }
        : null,
      lead: typeof q.lead === 'string' && q.lead.trim() ? q.lead.trim() : null,
    });
  }
  return questions.length > 0 ? questions : undefined;
}

/** True when a message's own lines carry an outcome worth a table — not a
 *  clarification pause, where every line is waiting on the customer and the
 *  question form is the whole UI. */
export function messageHasSettledLines(message: Message, data: any): boolean {
  const lines: RequestLine[] | undefined = (message as any).lines ?? data?.lines;
  const turn: string[] | undefined = (message as any).turn_lines ?? data?.turn_lines;
  if (!lines?.length || !turn?.length) return false;
  const inTurn = new Set(turn);
  return lines.some(l => inTurn.has(l.line_id)
    && ((l.status === 'needs_answer' && l.reason === 'SKIPPED')
      || (l.status !== 'needs_answer' && l.status !== 'searching' && l.status !== 'removed')));
}

/** Whether a bot message draws the results block (cards / table / compact
 *  unresolved list) under its text.
 *
 *  A clarification bubble is the exception: its question form is the whole UI.
 *  Even when one of its lines is already settled (a miss next to five waiting
 *  lines), drawing an "Unresolved" list of waiting rows under the question just
 *  restates the tracker and reads as a stray table (order 3214, 2026-09-09).
 *  The miss still shows in the tracker and on the answer turn's own results. */
export function showsResultsBlock(message: Message, data: any): boolean {
  const hasCards = Array.isArray(message.parts_results) && message.parts_results.length > 0;
  const hasSuggestions = !!data?.supplier_suggestions && Object.keys(data.supplier_suggestions).length > 0;
  if (hasCards || hasSuggestions) return true;
  const isClarification = Array.isArray(data?.clarification) && data.clarification.length > 0;
  if (isClarification) return false;
  return messageHasSettledLines(message, data)
    || (Array.isArray(data?.not_found_parts) && data.not_found_parts.length > 0);
}

/** Map a ValidationAgentResponse into the SummaryItem list used by the UI. */
function mapValidationResponseToSummaryItems(data: ValidationAgentResponse): SummaryItem[] {
  if (!data.parts?.length) return [];
  const items = data.parts.map((p): SummaryItem => {
    const warnings = p.warnings ?? [];
    let status: SummaryItem['status'] = 'normal';
    if (p.status === 'removed')        status = 'removed';
    else if (p.status === 'not_in_oe') status = 'not_in_oe';
    else if (p.status === 'edited')    status = 'edited';
    else if (warnings.length > 0)      status = 'warning';

    return {
      // `name` stays in the search language (corrected_name + canonical English
      // qualifiers) — it's the key used everywhere for matching result cards.
      // `displayLabel` is the backend-translated, client-language string the UI
      // SHOWS (falls back to name when the backend sent no display_label).
      name: buildPartDisplayName(p),
      displayLabel: buildPartDisplayLabel(p),
      originalQuery: p.original_name || p.name,
      status,
      warning: warnings.map(w => w.dimension).join(' · ') || undefined,
      oldName: p.old_name,
    };
  });
  return removeSupersededPartRows(items);
}

// A line that is a bullet: "- item", "* item" or "• item".
const BULLET_LINE = /^\s*[-*\u2022]\s+(.*)$/;

const renderInline = (line: string): string => line
  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  .replace(/\*([^*]+?)\*/g, '<em>$1</em>');

/** Lines → HTML: bold/italic inline, bullet lines grouped into a real list
 *  (the photo summary lists the parts it found), other lines joined by <br/>. */
function renderLines(text: string): string {
  const out: string[] = [];
  let inList = false;
  let pendingBreak = false;
  for (const line of text.split('\n')) {
    const bullet = BULLET_LINE.exec(line);
    if (bullet) {
      if (!inList) { out.push('<ul style="margin:6px 0;padding-left:20px;list-style:disc">'); inList = true; }
      out.push(`<li style="margin:2px 0">${renderInline(bullet[1])}</li>`);
      pendingBreak = false;
      continue;
    }
    if (inList) { out.push('</ul>'); inList = false; pendingBreak = false; }
    if (pendingBreak) out.push('<br/>');
    out.push(renderInline(line));
    pendingBreak = true;
  }
  if (inList) out.push('</ul>');
  return out.join('');
}

/** Convert basic markdown syntax to safe HTML for assessment_text rendering.
 *  Escapes HTML entities first to prevent XSS, then applies bold/italic,
 *  bullet lists and newlines. */
function renderMarkdown(text: string): string {
  return renderLines(text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;'));
}

// Like renderMarkdown but without HTML-escaping — for backend-generated content
// that may already contain HTML structure. Only call on trusted server content.
function renderMarkdownNoEscape(text: string): string {
  return renderLines(text);
}

// Apply markdown rendering to a text_message: converts **bold**/*italic* and
// newlines when the text is pure markdown (no HTML tags). Multi-line plain text
// (e.g. the QA node's numbered steps) also counts — without the newline→<br/>
// conversion the steps collapse into one wall of prose. Returns the text
// unchanged for HTML content.
function processTextMessage(text: string): string {
  if (!text) return text;
  // Media hygiene: raw media tags never render in prose, and a line that is
  // only an embeddable URL is dropped — MediaEmbeds renders both properly
  // below the message (it reads the ORIGINAL text, so nothing is lost).
  text = stripEmbeddableUrlLines(stripInlineMediaTags(text));
  const hasMarkdown = text.includes('**') || text.includes('*') || text.includes('\n');
  const hasHtml = /<\w/.test(text);
  return (hasMarkdown && !hasHtml) ? renderMarkdownNoEscape(text) : text;
}

const BotMessage: React.FC<BotMessageProps> = React.memo(({
  message,
  isLastMessage,
  isLoading,
  readOnly = false,
  addingToCartItems,
  onRetry,
  onFeedback,
  onSearchPart,
  onSendSilent,
  onSendMessage,
  previousClarifications,
  waitingNextQuestion,
  onSubmitClarification,
  onAddToCart: onAddToCartProp,
  formatTime,
  isValidImageData,
  onOrderUpdate,
  orderUpdateTrigger,
  cartItems,
  onSummaryItems,
  hideStreamingSteps = false,
  persistedAnnotatedImages,
  seenPartNumbers,
  priorParts,
  sessionHasLedger,
  onValidationParts,
  validationItems,
  isLatestResults,
  complementaryPending,
  // onOpenCartPanel / conversationalFollowups intentionally not destructured —
  // they only feed the chip UI, which is currently disabled (sentence-only CTA).
}) => {
  // A read-only viewer has no cart (the panel is hidden), so the results
  // table and suggestions drop their add buttons instead of dead controls.
  const onAddToCart = readOnly ? undefined : onAddToCartProp;
  const promptTakeOrder = useLockedOrderPrompt();

  const { t, i18n } = useTranslation();
  const { setActiveOrderId, getOrderObject } = useOrder();
  const isEmailSource = getOrderObject?.()?.source === 'email';
  const activeSessionId = useActiveSessionIdSafe();

  // Admin-only debug panel: gate on BOTH role and the debug toggle so a stale
  // localStorage flag can never leak model internals to a non-admin viewer.
  const { currentUser } = useAuth();
  const { debugMode } = useDebugMode();
  const showDebugPanel = debugMode && currentUser?.role === 'admin';
  // "took 12.3s" beside the timestamp of every finished turn — everyone.
  const showTurnTook = true;

  // Keep a stable ref to onSummaryItems so the effect doesn't re-run when the
  // callback reference changes (which would overwrite user edits/deletes).
  const onSummaryItemsRef = React.useRef(onSummaryItems);
  React.useEffect(() => { onSummaryItemsRef.current = onSummaryItems; }, [onSummaryItems]);
  const onValidationPartsRef = React.useRef(onValidationParts);
  React.useEffect(() => { onValidationPartsRef.current = onValidationParts; }, [onValidationParts]);
  // When the page hosts the floating tracker panel, the in-bubble card is suppressed.
  const usePanel = !!onValidationParts;

  // Track whether we've already fired summary items for this message to prevent
  // re-firing when other dependencies (isLastMessage, onOrderUpdate, etc.) change.
  const summaryItemsFiredForRef = React.useRef<string | null>(null);

  // Active results view ('cards' | 'table'), reported up from MultiPartResultView.
  // The bubble only widens for the table layout.
  const [partsViewMode, setPartsViewMode] = React.useState<'cards' | 'table'>('cards');
  // References panel: full excerpt passages behind the footer's
  // "Your reference documents" line (the tooltip only shows snippets).
  const [showReferencesModal, setShowReferencesModal] = React.useState(false);
  const [copiedExcerptIndex, setCopiedExcerptIndex] = React.useState<number | null>(null);
  const [zoomedImageIndex, setZoomedImageIndex] = React.useState<number | null>(null);
  // Any other image opened in the same zoom popup (email-intake attachments).
  const [zoomedSrc, setZoomedSrc] = React.useState<string | null>(null);
  const [typedIntentPreview, setTypedIntentPreview] = React.useState('');
  const [isIntentTypingComplete, setIsIntentTypingComplete] = React.useState(false);
  // Seeded from the assignment so a bubble rebuilt by a history reload shows
  // its marked photo on the first render, not one effect later.
  const [annotated_images, setAnnotatedImages] = React.useState<string[]>(() => persistedAnnotatedImages ?? []);
  const [isRepairExpanded, setIsRepairExpanded] = React.useState(false);
  const [summaryItems, setSummaryItems] = React.useState<SummaryItem[]>([]);
  const [summaryIntroText, setSummaryIntroText] = React.useState<string>('');
  // Computed synchronously (not via useState+useEffect) so the bubble is never
  // rendered even for a single frame before being hidden.
  const isOrderOnlyMessage = React.useMemo(() => {
    try {
      const trimmed = (message.text || '').trim();
      const raw = trimmed.startsWith('{')
        ? (JSON.parse(trimmed)?.text_message ?? message.text ?? '')
        : (message.text ?? '');
      return typeof raw === 'string' && raw.includes('ORDER_CREATED') && raw.includes('class="action"');
    } catch {
      return false;
    }
  }, [message.text]);
  const setIsOrderOnlyMessage = React.useCallback(() => {}, []); // kept for legacy call-sites in the effect below
  const [refinePopupItems, setRefinePopupItems] = React.useState<RefineItem[] | null>(null);
  const [selectedPartIndices, setSelectedPartIndices] = React.useState<Set<number>>(new Set());
  const hasTriggeredRef = React.useRef(false);
  const previousMessageIdRef = React.useRef(message.id);

  // Track whether this message has already finished its typing animation so
  // that re-renders (e.g. when isLastMessage flips) don't restart it.
  const typingDoneRef = React.useRef(false);
  const [typingAnimEnabled, setTypingAnimEnabled] = React.useState(false);
  // Stable snapshot of the cleaned text to animate — set once when streaming ends.
  const [animatedText, setAnimatedText] = React.useState('');
  // Track previous streaming state to detect the true→false transition.
  const wasStreamingRef = React.useRef(message.isStreaming ?? false);

  // Memoize parsed message data to avoid re-parsing on every render.
  // The validation agent wraps its ValidationAgentResponse inside an AgentResponse
  // envelope: { text_message: '{"is_greeting":false,"parts":[...],...}', execute_search: true, ... }.
  // We detect that pattern and expose both the outer envelope AND the inner
  // ValidationAgentResponse on a `validation` key for direct structured access.
  const message_data = React.useMemo(() => {
    // Helper: scan event history for an earlier validation envelope so the
    // parts-list summary stays visible even after a later search-result event
    // overwrites message.text with a different envelope.
    const findValidationFromEvents = (): ValidationAgentResponse | undefined => {
      const events = (message as any).events as Array<{ content?: string }> | undefined;
      if (!events) return undefined;
      for (const evt of events) {
        if (!evt?.content) continue;
        try {
          const parsed = JSON.parse(evt.content);
          // Direct validation response
          if (isValidationResponse(parsed)) return parsed;
          // Validation wrapped in an envelope { execute_search, text_message: "<json>" }
          if (parsed && typeof parsed === 'object' && typeof parsed.text_message === 'string') {
            try {
              const inner = JSON.parse(parsed.text_message);
              if (isValidationResponse(inner)) return inner;
            } catch { /* skip */ }
          }
        } catch { /* skip */ }
      }
      return undefined;
    };

    try {
      // Only attempt JSON parsing when the text looks like a JSON object.
      // Plain-text messages (e.g. "VIN KNAPV8…") are returned as-is to avoid
      // noisy SyntaxError exceptions in the console.
      const trimmed = (message.text || '').trim();
      if (!trimmed.startsWith('{')) {
        return { text_message: message.text || '' };
      }
      const outer = JSON.parse(trimmed);
      // Detect the validation envelope: outer has execute_search + text_message that is itself JSON
      if (
        outer &&
        typeof outer === 'object' &&
        'execute_search' in outer &&
        typeof outer.text_message === 'string'
      ) {
        try {
          const inner = JSON.parse(outer.text_message);
          if (isValidationResponse(inner)) {
            // Blank text_message: it holds the raw validation JSON, which must
            // never be treated as displayable text (it would defeat emptiness
            // checks and could leak JSON into the bubble).
            return { ...outer, text_message: '', validation: inner };
          }
          // Detect DataAgentResponse nested inside text_message
          if (isDataAgentResponse(inner)) {
            return { ...outer, text_message: inner.answer_html, dataAgent: inner };
          }
        } catch { /* inner text_message is not JSON — fall through */ }
      }
      // Detect ValidationAgentResponse at the top level (e.g. greeting envelopes
      // and history-loaded validations where the outer wrapper is gone). This
      // MUST come before the search-result envelope check below, because greeting
      // envelopes carry `session_part_uuids: []` which would otherwise match the
      // search-result detection and mis-route to an empty text_message.
      if (isValidationResponse(outer)) {
        return {
          execute_search: (outer as any).execute_search ?? true,
          text_message: '',
          validation: outer,
        };
      }
      // Detect clarification envelope: a top-level object carrying
      // `clarification_questions`. The parts agent emits this when it pauses to
      // ask the user to disambiguate one or more parts. The envelope's
      // text_message intro ("I found more than one match…") is DROPPED: it read
      // as a results announcement and primed users to mistake the candidate list
      // for found parts. The form's own question banner carries that framing now,
      // so the form IS the bubble.
      if (
        outer &&
        typeof outer === 'object' &&
        Array.isArray(outer.clarification_questions) &&
        outer.clarification_questions.length > 0
      ) {
        const clarification = normalizeClarificationQuestions(outer.clarification_questions);
        if (clarification) {
          return {
            ...outer,
            text_message: '',
            clarification,
          };
        }
      }
      // Detect search-result envelope: { order_id, selection_reason, requested_parts, ... }.
      // This is the FINAL envelope from a query_search turn — text_message is usually empty,
      // and the validation summary that was rendered earlier in the same turn is no longer
      // in message.text. Recover the validation response from message.events so the parts
      // list summary remains visible, and promote `selection_reason` so the loaded-parts
      // message is displayed.
      if (
        outer &&
        typeof outer === 'object' &&
        ('order_id' in outer || 'selection_reason' in outer) &&
        (outer.text_message === '' || outer.text_message === undefined || outer.text_message === null)
      ) {
        const recoveredValidation = findValidationFromEvents();
        const displayText =
          (typeof outer.selection_reason === 'string' && outer.selection_reason.trim()) ||
          '';
        return {
          ...outer,
          text_message: displayText,
          ...(recoveredValidation ? { validation: recoveredValidation } : {}),
        };
      }
      // Detect SyncAgentResponse envelope: { type, action, message, data }
      if (isSyncAgentResponse(outer)) {
          return { text_message: outer.message, syncAgent: outer as SyncAgentResponse };
      }
      // Detect raw vision-agent output: { identified_items: string[], vin_number: string|null }
      // The vision agent emits this JSON directly without the full SyncAgentResponse wrapper.
      if (
        outer &&
        typeof outer === 'object' &&
        Array.isArray(outer.identified_items) &&
        'vin_number' in outer &&
        !('type' in outer)
      ) {
        const syntheticSync: SyncAgentResponse = {
          type: 'extraction_result',
          action: null,
          message: '',
          data: {
            identified_items: outer.identified_items,
            vin_number: outer.vin_number ?? null,
          },
        };
        return { text_message: '', syncAgent: syntheticSync };
      }
      // Detect DataAgentResponse at the top level (direct from data_agent output_key)
      if (isDataAgentResponse(outer)) {
        return { text_message: outer.answer_html, dataAgent: outer };
      }
      // Detect data_agent envelope: { result: "<html>..." } — normalize to text_message
      if (
        outer &&
        typeof outer === 'object' &&
        typeof outer.result === 'string' &&
        !('text_message' in outer)
      ) {
        // result may itself be a DataAgentResponse JSON string
        try {
          const parsedResult = JSON.parse(outer.result);
          if (isDataAgentResponse(parsedResult)) {
            return { ...outer, text_message: parsedResult.answer_html, dataAgent: parsedResult };
          }
        } catch { /* not JSON — treat as plain HTML */ }
        return { ...outer, text_message: outer.result };
      }
      return outer;
    } catch (error) {
      console.error("Failed to parse message text:", error);
      return { text_message: message.text || '' };
    }
  }, [message.text, (message as any).events]);

  // Reset the trigger flag only when we get a genuinely NEW message ID
  React.useEffect(() => {
    if (previousMessageIdRef.current !== message.id) {
      hasTriggeredRef.current = false;
      summaryItemsFiredForRef.current = null;
      previousMessageIdRef.current = message.id;
      typingDoneRef.current = false;
      wasStreamingRef.current = message.isStreaming ?? false;
      setTypingAnimEnabled(false);
      setAnimatedText('');
      setSummaryItems([]);
      setSummaryIntroText('');
      // isOrderOnlyMessage is now a useMemo — no reset needed
      setRefinePopupItems(null);
      setSelectedPartIndices(new Set());
    }
  }, [message.id]);

  // Auto-trigger next iteration when NEXT marker is detected in the LAST message only (only once)
  React.useEffect(() => {
    // ── Structured order_id fast path ─────────────────────────────────────
    // The backend final response now carries `order_id` (and `session_part_uuids`)
    // directly in the structured JSON envelope, replacing the legacy
    // <a class="action">ORDER_CREATED</a> HTML marker. When present, fire the
    // same side effects (activate the order, refresh the cart) so the loaded
    // parts show up in the order panel.
    // Look for `order_id` first in the current top-level message_data, then
    // scan ALL events — a single bubble may receive multiple final events
    // (e.g. ORDER_CREATED side-effect final followed by a greeting final),
    // and `message.text` only holds the latest one.
    const findOrderIdFromEvents = (): string | number | undefined => {
      const events = (message as any).events as Array<{ content?: string }> | undefined;
      if (!events) return undefined;
      for (const evt of events) {
        if (!evt?.content) continue;
        try {
          const parsed = JSON.parse(evt.content);
          if (parsed && typeof parsed === 'object' && parsed.order_id != null) {
            return parsed.order_id;
          }
        } catch { /* skip */ }
      }
      return undefined;
    };
    const structuredOrderId = (message_data as any)?.order_id ?? findOrderIdFromEvents();
    if (structuredOrderId && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      const numericId = Number(structuredOrderId.toString().replace(/^PO-/i, ''));
      if (!Number.isNaN(numericId)) {
        setActiveOrderId?.(numericId);
      }
      setTimeout(() => { onOrderUpdate?.(); }, 100);
    }

    // ── Fast path: structured ValidationAgentResponse ─────────────────────
    // The backend now wraps the validation result in an AgentResponse envelope
    // where text_message contains the ValidationAgentResponse JSON.
    // message_data.validation is set by the memo above when that pattern is detected.
    const validationData: ValidationAgentResponse | undefined = message_data?.validation;
    if (validationData && !message.parts_results) {
      if (summaryItemsFiredForRef.current !== message.id) {
        const items = mapValidationResponseToSummaryItems(validationData);
        // The intro text is the whole bubble when validation holds the turn on a
        // question with NO parts ("Which left part do you need?") — it must not
        // wait for a parts list that never comes (blank bubble, 2026-09-19).
        setSummaryIntroText(validationData.assessment_text ?? '');
        if (items.length > 0) {
          summaryItemsFiredForRef.current = message.id;
          setSummaryItems(items);
          onSummaryItemsRef.current?.(
            items.filter(i => i.status !== 'removed' && i.status !== 'not_in_oe').map(i => i.name)
          );
          onValidationPartsRef.current?.({
            messageId: message.id,
            items,
            serviceTitle: validationData.service_title,
          });
        }
      }
      // No HTML to process — skip the legacy path entirely.
      return;
    }

    // ── Legacy path: HTML-based summary ───────────────────────────────────
    // Always parse fresh from message.text to avoid reading already-mutated message_data
    let raw_text: string;
    try {
      const trimmedText = (message.text || '').trim();
      raw_text = trimmedText.startsWith('{')
        ? (JSON.parse(trimmedText)?.text_message ?? '')
        : (message.text || '');
    } catch {
      raw_text = message.text || '';
    }

    if (!raw_text) {
      return;
    }

    let root_message_text = raw_text;

    const hasActionMarkers = root_message_text.includes('class="action"');

    let order_updated = false;
    let extractedOrderId: number | null = null;

    if (hasActionMarkers) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(root_message_text, 'text/html');
      const actionElements = doc.querySelectorAll('.action');
      
      actionElements.forEach((actionElement) => {
        switch (actionElement.textContent?.trim()) {
          // ORDER_CREATED is a client-side side-effect — fire regardless of
          // whether this bubble is the last message.  It may appear in an
          // intermediate continuation bubble that is no longer isLastMessage
          // because the next placeholder has already been appended.
          case 'ORDER_CREATED':
            const orderId = actionElement.getAttribute('data-orderid');
            if (orderId) {
              console.log('New Order Created with ID:', orderId);
              extractedOrderId = Number(orderId);
              setActiveOrderId?.(extractedOrderId);
            }
            order_updated = true;
            break;
          default:
            // Continuation actions (NEXT, TRIGGER_*, EXECUTE_SEARCH, STOP) are
            // handled server-side in the action loop — no client-side trigger needed.
            break;
        }
      });

      // REMOVE ALL FOUND ELEMENTS FROM THE ROOT MESSAGE TEXT TO CLEAN IT UP FOR DISPLAY
      actionElements.forEach((el) => {
        root_message_text = root_message_text?.replace(el.outerHTML, '') ?? root_message_text;
      });

      if (order_updated) {
        if (!hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          // isOrderOnlyMessage is now derived from message.text via useMemo — no setter needed.
          setTimeout(() => {
            if (onOrderUpdate && order_updated) {
              onOrderUpdate();
            }
          }, 100);
        }
      }   
    }

    const summary_message = root_message_text && root_message_text.includes('custom-list parts-list') && !message.parts_results;
    if (summary_message) {
      // Only fire once per message ID to avoid overwriting user edits/deletes
      // when other deps (isLastMessage, onOrderUpdate, etc.) cause the effect to re-run.
      if (summaryItemsFiredForRef.current === message.id) {
        // Already sent items for this message — do not overwrite user changes.
      } else {
        const listItemMatches = root_message_text.match(/<li[^>]*class="[^"]*list-item[^"]*"[^>]*>[\s\S]+?<\/li>/g);
        if (listItemMatches && listItemMatches.length > 0) {
          const items = listItemMatches
            .map((liHtml: string) => parsePartListItem(liHtml))
            .filter((item): item is SummaryItem => item !== null && item.status !== 'removed');

          if (items.length > 0) {
            summaryItemsFiredForRef.current = message.id;
            setSummaryItems(items);
            // Strip the <ul class="custom-list parts-list">…</ul> block so we can render it styled
            const introOnly = root_message_text.replace(/<ul[^>]*class="[^"]*parts-list[^"]*"[^>]*>[\s\S]*?<\/ul>/gi, '').trim();
            setSummaryIntroText(introOnly);
            // Only forward non-removed items to the parent as actionable parts
            onSummaryItemsRef.current?.(
              items.filter(i => i.status !== 'removed').map(i => i.name)
            );
            onValidationPartsRef.current?.({ messageId: message.id, items });
          }
        }
      }
    }

  }, [message.isStreaming, message.text, message_data, setActiveOrderId, onOrderUpdate]);

  // Latch the typing animation once: fires when streaming transitions true → false.
  // Using wasStreamingRef means this works even if isLastMessage has already flipped
  // by the time the render cycle runs (which happens when the next message arrives
  // immediately after this one finishes).
  React.useEffect(() => {
    const justFinished = wasStreamingRef.current && !message.isStreaming;
    wasStreamingRef.current = message.isStreaming ?? false;

    if (justFinished && !typingDoneRef.current) {
      // message_data.text_message was already cleaned up by the action-processing
      // effect above (same dep array, runs first). Snapshot it now.
      const cleanedText = message_data.text_message || '';
      if (cleanedText) {
        setAnimatedText(processTextMessage(cleanedText));
        setTypingAnimEnabled(true);
      }
    }
  }, [message.isStreaming, message_data.text_message]);

  // Show intent preview immediately without typing animation
  React.useEffect(() => {
    if (message.intentPreview) {
      setTypedIntentPreview(message.intentPreview);
      setIsIntentTypingComplete(true);
    } else {
      setTypedIntentPreview('');
      setIsIntentTypingComplete(false);
    }
  }, [message.intentPreview]);

  // Show call to action immediately without typing animation
  // Which annotated images THIS bubble owns is decided by the parent
  // (`buildAnnotatedImageAssignments`), never by the message alone: the same
  // marked photo rides along on several messages of one turn and is re-signed on
  // every history reload, so deciding per-bubble rendered it repeatedly.
  React.useEffect(() => {
    const assigned = persistedAnnotatedImages ?? [];
    setAnnotatedImages(prev =>
      prev.length === assigned.length && prev.every((u, i) => u === assigned[i]) ? prev : assigned,
    );
  }, [persistedAnnotatedImages]);

  // Memoize computed values
  const isVisionAnalysis = React.useMemo(
    () => message.structured_data?.task_type === 'vision_analysis' &&
      message.parts_list && message.parts_list.length > 0,
    [message.structured_data?.task_type, message.parts_list]
  );

  // Typing effect for the main text content (only on the last, non-streaming message).
  const { displayedHtml: typedMessageHtml, isDone: typingIsDone } = useHtmlTypingEffect(
    animatedText,
    typingAnimEnabled,
  );

  // Once done, mark so subsequent re-renders skip the animation.
  React.useEffect(() => {
    if (typingIsDone && typingAnimEnabled) {
      typingDoneRef.current = true;
    }
  }, [typingIsDone, typingAnimEnabled]);

  // Use the onRetry prop directly - parent handles finding previous user message
  const handleRetryFromBot = React.useCallback(() => {
    onRetry(message);
  }, [message, onRetry]);

  const hasClarificationQuestions =
    Array.isArray((message_data as any).clarification) && (message_data as any).clarification.length > 0;

  // Resolve each clarification question's header to the SAME label shown in the
  // requested-parts tracker (e.g. "Óptico, Right (Optico derecho)"), so the
  // question reads identically to the list. Matches the question to its tracker
  // row by name with the exact matcher the cards use; falls back to the backend
  // part_label, then the bare part_name. (Kept above the early returns below so
  // the hook order never changes.)
  const clarificationHeaderLabels = React.useMemo(() => {
    const questions: ClarificationQuestion[] | undefined = (message_data as any).clarification;
    const map: Record<string, string> = {};
    if (!questions) return map;
    // The line waiting on a question names it. That is a lookup, not a match:
    // the ledger records which question each paused line is waiting for.
    const lines: RequestLine[] = (message as any).lines ?? (message_data as any).lines ?? [];
    // Same "Label (original wording)" string the tracker row shows, so the
    // header names what the customer actually typed next to the resolved name.
    const labelByQuestion = new Map(
      lines.filter(l => l.question_id).map(l => [l.question_id as string, trackerItem(l).displayLabel]),
    );
    for (const q of questions) {
      map[q.id] = dedupeLabelQualifiers(labelByQuestion.get(q.id) || q.part_label || q.part_name);
    }
    return map;
  }, [message_data, message]);
  // The same line split in two — what the customer typed and our reading of
  // it — so the question header can show “original” → label.
  // Questions the customer skipped this turn: shown above the result with
  // "Answer now"; the table row can open one in its diagram or the AI chat.
  const postponedQuestions: ClarificationQuestion[] = React.useMemo(
    () => normalizeClarificationQuestions((message_data as any)?.postponed_questions) || [],
    [message_data],
  );
  const [postponedOpen, setPostponedOpen] = React.useState<Record<string, { mode: PostponedOpenMode; nonce: number }>>({});
  const openPostponed = React.useCallback((questionId: string, mode: PostponedOpenMode) => {
    setPostponedOpen(prev => ({ ...prev, [questionId]: { mode, nonce: Date.now() } }));
  }, []);
  const clarificationRequestLines = React.useMemo(() => {
    const lines: RequestLine[] = (message as any).lines ?? (message_data as any).lines ?? [];
    const map: Record<string, { original: string; label: string }> = {};
    for (const l of lines) {
      if (l.question_id) map[l.question_id] = { original: l.original, label: dedupeLabelQualifiers(l.label || l.canonical || l.original) };
    }
    return map;
  }, [message_data, message]);

  // "Hint" on a clarification question: ask the backend for an AI note that
  // helps the user tell the candidates apart. Only for signed-in users — guest
  // and portal pages have no Firebase auth for the endpoint, so the form gets no
  // callback there and simply shows no button. The header label (the same
  // string as the requested-parts row) is the requested part the model is told
  // about; the order's vin_data is the fallback vehicle when the session can't
  // be resolved server-side.
  const requestClarificationHint = React.useCallback(async (q: ClarificationQuestion): Promise<ClarificationHint> => {
    const order = getOrderObject?.();
    return chatService.getClarificationHint({
      session_id: activeSessionId || chatService.getGuestSessionId() || null,
      question_id: q.id,
      part_name: clarificationHeaderLabels[q.id] || q.part_label || q.part_name,
      part_parameters: q.part_parameters ?? [],
      options: (q.options || []).map(o => ({
        id: o.id,
        label: o.label,
        callout: o.load?.[0]?.callout ?? null,
        part_number: o.load?.[0]?.part_number ?? null,
        extra_details: o.extra_details ?? null,
        probability_score: o.probability_score ?? null,
        section_key: o.load?.[0]?.group_id && o.load?.[0]?.section_id ? `${o.load[0].group_id}/${o.load[0].section_id}` : null,
        section_label: o.section_label ?? null,
      })),
      vehicle: order?.vin_data ?? null,
      language: i18n.language || null,
    });
  }, [getOrderObject, activeSessionId, clarificationHeaderLabels, i18n.language]);
  // Anyone who can OPEN the order gets the hints — they are read-only, cached
  // server-side per candidate set, and the tags are half of what the question
  // says. Only the live (last) message asks: an older question either carries
  // the guidance saved with its answer or belongs to an abandoned branch.
  const onRequestHint = currentUser && isLastMessage ? requestClarificationHint : undefined;

  // Chat about one clarification question (side panel). Same inputs as the
  // hint plus the conversation; the server prefers its own copy of the
  // pending question. Signed-in users on the live question only.
  const chatAboutQuestion = React.useCallback(async (q: ClarificationQuestion, messages: ClarificationChatTurn[]) => {
    const order = getOrderObject?.();
    return chatService.clarificationChat({
      session_id: activeSessionId || chatService.getGuestSessionId() || null,
      question_id: q.id,
      question_text: q.text || null,
      part_name: clarificationHeaderLabels[q.id] || q.part_label || q.part_name,
      part_parameters: q.part_parameters ?? [],
      options: (q.options || []).map(o => ({
        id: o.id,
        label: o.label,
        callout: o.load?.[0]?.callout ?? null,
        part_number: o.load?.[0]?.part_number ?? null,
        extra_details: o.extra_details ?? null,
        probability_score: o.probability_score ?? null,
        section_key: o.load?.[0]?.group_id && o.load?.[0]?.section_id ? `${o.load[0].group_id}/${o.load[0].section_id}` : null,
        section_label: o.section_label ?? null,
      })),
      messages,
      vehicle: order?.vin_data ?? null,
      language: i18n.language || null,
    });
  }, [getOrderObject, activeSessionId, clarificationHeaderLabels, i18n.language]);
  const onChatAboutQuestion = currentUser && isLastMessage ? chatAboutQuestion : undefined;
  const loadChatHistory = React.useCallback(async (q: ClarificationQuestion) => {
    const sid = activeSessionId || chatService.getGuestSessionId();
    return sid ? chatService.clarificationChatHistory(sid, q.id) : [];
  }, [activeSessionId]);
  const onLoadChatHistory = currentUser && isLastMessage ? loadChatHistory : undefined;
  // The side chat changes the requested parts through the same hidden edit
  // command as the tracker's "Edit parts" (silent send; the history shows the
  // compact "Part search updated" indicator instead of a user bubble).
  const partEdit = React.useMemo(
    () => (onSendSilent && !readOnly
      ? (edits: PartEdit[]) => { const m = buildPartEditMarker(edits); if (!m.endsWith('%5B%5D')) onSendSilent(m); }
      : undefined),
    [onSendSilent, readOnly],
  );
  const clarificationChatContext = React.useMemo(() => {
    const order = getOrderObject?.() as any;
    const bits = [order?.vin, order?.external_order_id || order?.order_number].filter(Boolean);
    return bits.length ? bits.join(' · ') : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOrderObject, message]);

  // A CTA-only turn ships an EMPTY text_message on purpose (the sentence
  // renders once, in the SuggestedActions panel below — not in the bubble).
  // Such a message must not be dropped here; the bubble body itself stays
  // hidden via the content gates further down.
  const hasCtaOnly = !!String((message as any).callToAction || (message_data as any)?.callToAction || '').trim();
  const hasDataSourceSuggestions = !!(
    (message_data as any)?.supplier_suggestions
    && Object.keys((message_data as any).supplier_suggestions).length > 0
  );
  if ((!message_data.text_message && !message_data.validation && !message_data.syncAgent && !hasClarificationQuestions && !message.isStreaming && !message.repair_instructions && (!message.parts_results || message.parts_results.length === 0) && (!message.parts_list || message.parts_list.length === 0) && !message.ext_search_results && !hasCtaOnly && !hasDataSourceSuggestions) || isOrderOnlyMessage) {
    return null;
  }

  // NOTE: Do NOT suppress is_greeting=true bubbles here — the automotive expert's
  // greeting ("Hi! I'm your service adviser agent…") uses is_greeting=true with
  // empty assessment_text and relies on hardcoded i18n text in this component.
  // Empty continuation placeholders are already dropped in useStreamSend's onComplete
  // via the !hasFinalEvent && !hasText check.


  if (shouldHideSyncAgentMessage(message_data.syncAgent, !!message.isStreaming)) {
    // Hidden sync-agent message — annotated images from this bubble are forwarded
    // to the next validation bubble via the `persistedAnnotatedImages` prop (managed
    // by the parent).  Nothing to render here.
    return null;
  }

  const hasSyncAgentOnly = !message.isStreaming && !!(
    message_data.syncAgent &&
    !message_data.validation &&
    (!message.parts_results || message.parts_results.length === 0) &&
    (!message.parts_list || message.parts_list.length === 0) &&
    !message.ext_search_results
  );
  // When the floating tracker panel owns the parts list, a validation bubble only
  // has content of its own if it carries assessment text or annotated images
  // (greetings keep their dedicated branch). Without the panel (guest/portal),
  // legacy behavior: any validation renders the in-bubble card.
  // Annotated images render in their own bubble above this one (see
  // `showAnnotatedBlock`), so they no longer make an otherwise-empty validation
  // bubble worth rendering.
  const hasAnnotatedImagesContent = false;
  // A text_message can be truthy yet render to nothing (whitespace, empty HTML
  // tags). Only count it as content when something would actually be visible.
  const strippedTextMessage = (message_data.text_message || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const hasVisibleTextMessage =
    !!strippedTextMessage || /<(img|svg|video|iframe)\b/i.test(message_data.text_message || '');
  // Derive the validation assessment text directly from message_data so the
  // visibility decision is synchronous from the first render (independent of
  // the `summaryIntroText` state, which is populated by an effect one tick later).
  const validationAssessmentText =
    (message_data.validation?.assessment_text || '').trim();
  // Use the parsed validation parts directly (synchronous) instead of the
  // `summaryItems` state, which lags by one render cycle.
  const validationHasParts =
    !!(message_data.validation?.parts && message_data.validation.parts.length > 0);
  // Legacy HTML summary message whose parts list now lives in the panel: the
  // bubble only shows the stripped intro text, which may well be empty.
  const legacyPanelSummary = usePanel && !message_data.validation && summaryItems.length > 0;
  const validationBubbleContent = usePanel && message_data.validation && !message_data.validation.is_greeting
    // Panel mode: parts are lifted to the floating tracker, so the bubble only
    // earns the right to render when it carries assessment text, annotated
    // images, or a non-empty text fallback that the summary card won't swallow.
    // When the validation has parts but no assessment text and no images, the
    // bubble would be a blank shell — suppress it entirely.
    ? !!(validationAssessmentText ||
         hasAnnotatedImagesContent ||
         // selection_reason-style text on a recovered-validation envelope is only
         // rendered by the generic branch, i.e. when no summary card swallowed it.
         // Only count it when validation has NO parts (parts would feed the summary card).
         (!validationHasParts && hasVisibleTextMessage))
    : legacyPanelSummary
      ? !!(summaryIntroText.trim() || hasAnnotatedImagesContent)
      // Guest/portal (no panel): validation always renders the in-bubble card,
      // but if validation has neither parts nor assessment text nor images,
      // there's nothing to show — fall back to the visible-text check only.
      : message_data.validation
        ? !!(validationAssessmentText || validationHasParts || hasAnnotatedImagesContent || hasVisibleTextMessage)
        : !!hasVisibleTextMessage;
  // Structured clarification questions surfaced from the envelope (parts agent pause).
  const clarificationQuestions: ClarificationQuestion[] | undefined = (message_data as any).clarification;
  const hasClarification = !!(clarificationQuestions && clarificationQuestions.length > 0);
  // Stored answers re-attached by the backend on history reload (keyed by question
  // id). When present the form renders pre-selected + locked instead of fresh.
  const clarificationAnswers: Record<string, { selected_option_ids?: string[]; free_text?: string | null }> | undefined =
    (message_data as any).clarification_answers;
  // Once the conversation advances, an unanswered clarification belongs to an
  // old branch. History can contain both that orphaned question event and the
  // later answered copy; hide the orphan completely so the submitted response
  // is the single visible record of the interaction.
  const isSupersededClarification = Boolean(
    hasClarification && !clarificationAnswers && !isLastMessage,
  );
  // NOTE: validationBubbleContent must stand ALONE here — parsed validation
  // payloads deliberately blank text_message (it held raw JSON), so gating on
  // `validationBubbleContent && text_message` made a validation-TERMINAL turn
  // (e.g. not_in_oe with execute_search=false) render NOTHING despite a
  // non-empty assessment_text (live: 'Engine oil 5W-30 synthetic' got no
  // reply at all, session s1).
  const hasVisibleClarification = hasClarification && !isSupersededClarification;
  const hasMainContent = !hasSyncAgentOnly && !!(validationBubbleContent || hasVisibleClarification || message.isStreaming || message.repair_instructions || (message.parts_results && message.parts_results.length > 0) || (message.parts_list && message.parts_list.length > 0));
  // hasRenderableContent: anything to show in the white content bubble (NOT just streaming).
  // When this is false and `message.isStreaming` is true, only the thinking-indicator bubble
  // renders, avoiding an empty duplicate bubble while the agent is still working.
  // When syncAgent is present but there is no other renderable content (no parts, no validation,
  // no repair instructions), the bubble content section is fully hidden by the !syncAgent guard
  // inside, which would produce an empty white bubble. Exclude that case here so the syncAgent
  // result is only shown in its own dedicated bubble below.
  const hasRenderableContent = !hasSyncAgentOnly && !!(validationBubbleContent || hasVisibleClarification || message.repair_instructions || (message.parts_results && message.parts_results.length > 0) || (message.parts_list && message.parts_list.length > 0));
  // The results table is the only layout that needs the extra room, so the bubble
  // widens only while the user has the table view selected; cards and plain text
  // replies stay compact.
  // The bubble widens for the results table — and for the compact unresolved
  // list a miss-only turn renders in its place, which needs the same room for
  // its wording, its explanation and its two actions. Cards mode stays narrow.
  const hasCards = !!(message.parts_results && message.parts_results.length > 0);
  const hasWidePartsContent = hasCards
    ? partsViewMode === 'table'
    : messageHasSettledLines(message, message_data)
      || !!(message_data.not_found_parts && message_data.not_found_parts.length > 0);
  // Bubble measure: the results table takes the full column, a clarification
  // card a medium one (chips + a readable drawing), text the narrow reading width.
  const bubbleWidth = hasWidePartsContent
    ? 'sm:max-w-[38rem] lg:max-w-[50rem] xl:max-w-full'
    : hasClarificationQuestions
      ? 'sm:max-w-[38rem] lg:max-w-[42rem]'
      : 'sm:w-80 lg:w-96 xl:w-[28rem]';

  // The markings belong in the conversation itself. SyncAgentResultView renders
  // its own copy for the bubbles it owns, so skip those to avoid a double image.
  const showAnnotatedBlock =
    annotated_images.length > 0 &&
    !(message_data.syncAgent && isSyncAgentResponseShouldDisplay(message_data.syncAgent));

  return (
    <>
      {hasMainContent && <div className="flex gap-2 sm:gap-3 flex-col max-w-full">
        <div className="flex gap-2 sm:gap-3 flex-row">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center flex-shrink-0 bg-transparent">
            {message.isStreaming ? (
              <DotLottieReact
                src={getVersionedPublicUrl('/cata_loader.lottie')}
                loop
                autoplay
                style={{ width: 32, height: 32 }}
              />
            ) : (
              <img src={getVersionedPublicUrl('/32x32/favicon.svg')} alt="Cata" className="w-6 h-6 sm:w-8 sm:h-8" />
            )}
          </div>

          {/* In table mode the bubble grows to the FULL width of the chat column
              (max-w-4xl on the list, minus the avatar) instead of stopping short
              of it — the results table has 10+ columns and every extra pixel goes
              to the wrapping description/requested cells. Cards and text replies
              keep the narrow reading measure. */}
          <div className={`w-full flex flex-col gap-2 min-w-0 ${bubbleWidth}`}>
            {/* Content bubble — rendered first so summary appears above the thinking indicator.
                Only render when there is actual content to show; otherwise the streaming
                indicator bubble below acts as the sole bubble for this message. */}
            {hasRenderableContent && (
              <div className={`p-2 sm:p-3 rounded-2xl card-enter shadow-sm break-words overflow-hidden ${message.isError ? 'bg-red-500 text-white' : 'bg-white text-gray-900'} ${message_data.ai_generated === true && !message.isError ? 'border border-amber-300' : ''}`}>
              {/* Message content - summary card always shown when summaryItems exist; regular text only shown when no parts */}
              {(summaryItems.length > 0 || ((!message.parts_list || message.parts_list.length === 0) && (!message.parts_results || message.parts_results.length === 0))) && !message_data.syncAgent && (
              <div className="request-content">
                {/* Styled summary card — replaces raw HTML rendering for summary messages */}
                {summaryItems.length > 0 ? (
                  <>
                    {/* Refine popup — legacy in-bubble flow only (panel hosts its own) */}
                    {!usePanel && refinePopupItems !== null && (
                      <RefinePopup
                        items={refinePopupItems}
                        t={t}
                        onSubmit={(refined) => {
                          if (readOnly) { promptTakeOrder?.(); return; }
                          const msg = buildPartEditMarker(refined);
                          if (!msg.endsWith('%5B%5D')) {
                            if (onSendSilent) onSendSilent(msg);
                            else onSearchPart?.(msg);
                          }
                          setSelectedPartIndices(new Set());
                        }}
                        onClose={() => setRefinePopupItems(null)}
                      />
                    )}
                    <div style={{ overflow: 'hidden'}}>
                      {/* Assessment text — plain text from structured response, or legacy HTML.
                          The bottom border only makes sense when the parts list renders
                          below it (legacy in-bubble card); with the tracker panel active
                          the text is the last element, so no divider. */}
                      {summaryIntroText && (
                        <div style={{ padding: usePanel ? '4px 2px' : '10px 14px 8px 14px', borderBottom: usePanel ? 'none' : '1px solid #e5e3df' }}>
                          {message_data?.validation ? (
                            <div className="font-jakarta text-message" style={{ margin: 0, color: '#333' }}
                              dangerouslySetInnerHTML={{ __html: renderMarkdown(summaryIntroText) }}
                            />
                          ) : (
                            <p className="font-jakarta text-message" style={{ margin: 0, color: '#333' }}
                              dangerouslySetInnerHTML={{ __html: summaryIntroText }}
                            />
                          )}
                        </div>
                      )}
                      {/* Parts list card — only when no floating tracker panel owns it */}
                      {!usePanel && (<>
                      {/* Section label row — Parts List label + Refine button */}
                      <div style={{ padding: '8px 12px 2px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 }}>
                          {t('chat.botMessage.partsSummary', { defaultValue: 'Parts to Search' })}
                        </span>
                        {/* Single Refine button — only shown when not loading (streaming done, user's turn) */}
                        {!isLoading && (
                        <button
                          onClick={() => {
                            const activeItems = summaryItems
                              .map((item, idx) => ({ item, idx }))
                              .filter(({ item }) => item.status !== 'removed');
                            const toRefine = activeItems.filter(({ idx }) => selectedPartIndices.has(idx));
                            const source = toRefine.length > 0 ? toRefine : activeItems;
                            setRefinePopupItems(source.map(({ item }) => {
                              const { partName, ref } = splitPartAndRef(item.displayLabel || item.name);
                              const term = item.originalQuery || ref || item.name;
                              // The line's label is the edit's identity (see MultiPartResultView.editResult).
                              const label = partName || term;
                              const identity = isPartNumberQuery(term) ? term : label;
                              return { original: identity, value: label, label };
                            }));
                          }}
                          style={{
                            flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '2px 8px', fontSize: '10px', fontWeight: 600,
                            color: '#6866ff', backgroundColor: 'transparent',
                            border: '1px solid #c5c4ff', borderRadius: '20px',
                            cursor: 'pointer', lineHeight: '14px', whiteSpace: 'nowrap',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#efedff')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                          title={t('chat.botMessage.refineParts', { defaultValue: 'Refine parts' })}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                          {selectedPartIndices.size > 0
                            ? t('chat.botMessage.refineSelected', { defaultValue: 'Refine selected' })
                            : t('chat.botMessage.refineParts', { defaultValue: 'Refine' })}
                        </button>
                        )}
                      </div>
                      {/* Items */}
                      <ul
                        className="notranslate"
                        // translate="no": browser auto-translate rewrites text nodes in
                        // these rows with <font> wrappers and breaks React reconciliation
                        // ("insertBefore … not a child of this node"). Content is already
                        // localized server-side via client_language.
                        translate="no"
                        style={{ margin: 0, padding: '4px 12px 10px 12px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '2px' }}
                      >
                        {(() => {
                          // Mark summary items as 'in_cart' when a matching cart part exists
                          // (shared matching helpers live with the tracker panel).
                          const cartNameList = buildCartNameList(cartItems);

                          // Stable React keys by part identity (name + occurrence), not
                          // index: the cumulative list reorders/merges between turns, and
                          // index keys make React reuse <li> subtrees across different
                          // parts — crashing reconciliation when row shape changes.
                          const seenKeys = new Map<string, number>();

                          return summaryItems.map((item, idx) => {
                            const effectiveItem: SummaryItem =
                              item.status !== 'removed' &&
                              item.status !== 'not_in_oe' &&
                              item.status !== 'in_cart' &&
                              isInCartByName(item.name, cartNameList)
                                ? { ...item, status: 'in_cart' }
                                : item;

                            const baseKey = `${item.status === 'removed' ? 'removed:' : ''}${(item.name || '').toLowerCase().trim()}`;
                            const occurrence = seenKeys.get(baseKey) ?? 0;
                            seenKeys.set(baseKey, occurrence + 1);

                            return (
                              <SummaryPartItem
                                key={occurrence ? `${baseKey}~${occurrence}` : baseKey}
                                item={effectiveItem}
                                t={t}
                                isSelected={selectedPartIndices.has(idx)}
                                showCheckbox={false}
                                onToggleSelect={item.status !== 'removed' ? () => {
                                  setSelectedPartIndices(prev => {
                                    const next = new Set(prev);
                                    next.has(idx) ? next.delete(idx) : next.add(idx);
                                    return next;
                                  });
                                } : undefined}
                              />
                            );
                          });
                        })()}
                      </ul>
                      {/* Service title badge — full width below the header row */}
                      {message_data?.validation?.service_title && (
                        <div style={{ padding: '3px 12px 0 12px' }}>
                          <span style={{
                            display: 'inline-block',
                            fontSize: '10px', color: '#6866ff', backgroundColor: '#ededff',
                            border: '1px solid #c7c6ff', borderRadius: '20px',
                            padding: '2px 10px', maxWidth: '100%',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {t('chat.botMessage.serviceType', { defaultValue: 'Service Type' })}: {t(`orders.serviceTypes.${message_data.validation.service_title}`, { defaultValue: message_data.validation.service_title })}
                          </span>
                        </div>
                      )}
                      </>)}
                    </div>
                  </>
                ) : (
                  <>
                  <section className="section-label">
                    {message_data.syncAgent ? (
                      <SyncAgentResultView
                        response={message_data.syncAgent}
                        onImageClick={setZoomedImageIndex}
                      />
                    ) : (message_data?.validation?.is_greeting || message_data?.validation?.assessment_text) ? (
                      // Validation-agent greeting (CASE 1 in validation_agent.md) — or a
                      // validation frame that holds the turn on a question with NO parts
                      // ("Which left part do you need?"): its assessment_text is the whole
                      // bubble, and the generic branch below renders text_message, which
                      // is empty for validation frames (blank bubble, 2026-09-19).
                      //
                      // `assessment_text` is now the single source of truth for the
                      // greeting prompt — the agent already writes the
                      // acknowledgement + "what parts do you need?" question
                      // (and references the decoded vehicle when available) in
                      // the user's locale. Rendering the hard-coded
                      // `chat.greeting.identified` / `chat.greeting.searchHint`
                      // prose *in addition* duplicated the same message inside
                      // every greeting bubble (see screenshot in
                      // feature/refactoring-2.0). Fall back to the localised
                      // strings only when the agent produced no text at all.
                      <div className="prose prose-sm max-w-none font-jakarta text-message">
                        {message_data.validation.assessment_text ? (
                          <div style={{ margin: 0, color: '#333' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(message_data.validation.assessment_text) }} />
                        ) : (
                          <>
                            <p style={{ margin: 0, color: '#333' }}>
                              {t('chat.greeting.hello', { defaultValue: 'Hi' })}{' '}
                              {t('chat.greeting.serviceAdviser', { defaultValue: "I'm your service adviser agent" })}.
                            </p>
                            <br />
                            <p style={{ margin: 0, color: '#333' }}>{t('chat.greeting.identified', { defaultValue: 'I have identified your vehicle. What part or service are you looking for today?' })}</p>
                            <p style={{ margin: 0, color: '#333' }}>{t('chat.greeting.searchHint', { defaultValue: 'Search by description (e.g., "I need oil filters") or part #' })}</p>
                          </>
                        )}
                      </div>
                    ) : message_data?.dataAgent ? (
                      <div className="data-agent-host font-jakarta text-message" style={{ margin: 0, color: '#333' }}>
                        <div
                          dangerouslySetInnerHTML={{ __html: (typingAnimEnabled && !typingIsDone)
                            ? typedMessageHtml + '<span class="typing-cursor" aria-hidden="true"></span>'
                            : processTextMessage(message_data.text_message || '') }}
                        />
                      </div>
                    ) : (
                    // While the bubble is still streaming, suppress raw text rendering
                    // for the generic fallback branch. Intermediate frames may carry
                    // partial JSON envelopes (e.g. a search-result envelope being
                    // assembled) whose `text_message` flickers into the bubble for a
                    // few frames before the final arrives and the parts grid replaces
                    // the whole content. The "Thinking…" indicator bubble below
                    // already covers the in-progress state.
                    
                    <div
                      dangerouslySetInnerHTML={{ __html: (typingAnimEnabled && !typingIsDone)
                        ? typedMessageHtml + '<span class="typing-cursor" aria-hidden="true"></span>'
                        : processTextMessage(message_data.text_message || '') }}
                      className={`${(message_data.text_message || '').includes('data-agent-answer') ? 'data-agent-host' : 'prose prose-sm max-w-none'} font-jakarta text-message`}
                      style={{ margin: 0, color: '#333' }}
                    />
                    )}
                  </section>
                  {/* Visual sources referenced by the answer (YouTube, image
                      or video links — in the text or in answer_sources) are
                      additionally rendered inline; the links in the text stay
                      untouched. MUST live OUTSIDE the <section>: .section-label
                      is display:flex (row), which squeezed the embeds into a
                      tiny column beside the text. Waits for streaming/typing
                      to finish so partial URLs never produce a broken embed. */}
                  {!message.isStreaming && (!typingAnimEnabled || typingIsDone) && (
                    <MediaEmbeds
                      text={message_data.text_message || ''}
                      sources={Array.isArray(message_data.answer_sources) ? message_data.answer_sources : []}
                    />
                  )}
                  {/* General-QA reference footer: one labeled line per source
                      class, in plain user language — "Your vehicle: …", "From
                      this session's search results: …" — never a raw data dump.
                      Rendered under a hairline divider; the general-AI notice
                      closes the message. Older messages without answer_sources
                      fall back to the notice alone. */}
                  {(message_data.ai_generated === true
                    || (Array.isArray(message_data.answer_sources) && message_data.answer_sources.length > 0)) && (() => {
                    const sources: any[] = Array.isArray(message_data.answer_sources) ? message_data.answer_sources : [];
                    const partsSource = sources.find((s) => s?.type === 'parts');
                    // Preferred: one explanatory sentence (`note`). Legacy stored
                    // messages carry a `details` name list — degrade to a join.
                    const partsNote: string = typeof partsSource?.note === 'string' && partsSource.note
                      ? partsSource.note
                      : (Array.isArray(partsSource?.details)
                          ? partsSource.details.filter((d: any) => typeof d === 'string' && d).slice(0, 3).join(' · ')
                          : '');
                    const hasCatalog = sources.some((s) => s?.type === 'catalog');
                    const hasGeneral = sources.some((s) => s?.type === 'general') || sources.length === 0;
                    const webLabel = (s: any) => {
                      if (s.title) return s.title;
                      try { return new URL(s.url).hostname.replace(/^www\./, ''); } catch { return s.url; }
                    };
                    // Dedupe web citations by displayed site — grounding returns
                    // distinct redirect URLs for the same site (also covers
                    // messages stored before the backend deduped).
                    const webSources: any[] = [];
                    const seenSites = new Set<string>();
                    for (const s of sources) {
                      if (s?.type !== 'web' || typeof s?.url !== 'string' || !s.url) continue;
                      const site = webLabel(s).toLowerCase();
                      if (seenSites.has(site)) continue;
                      seenSites.add(site);
                      webSources.push(s);
                      if (webSources.length >= 3) break;
                    }
                    const lineStyle: React.CSSProperties = {
                      display: 'flex', alignItems: 'flex-start', gap: '6px',
                      fontSize: '11px', lineHeight: 1.5, color: '#4b5563',
                    };
                    const lineIcon: React.CSSProperties = {
                      width: '13px', height: '13px', flexShrink: 0, marginTop: '2px',
                    };
                    const labelStyle: React.CSSProperties = { fontWeight: 600, color: '#374151' };
                    const lines: React.ReactNode[] = [];
                    {/* Client-provided enrichment (order extra_data) — the
                        authoritative source, so it leads the footer. */}
                    const enrichmentSource = sources.find((s) => s?.type === 'enrichment');
                    // Org-provided sources (the customer's own documents)
                    // are VERIFIED data — badge them so the user knows this
                    // part of the answer can be relied on, in contrast to
                    // the general-AI-knowledge disclaimer below.
                    const verifiedChip = (
                      <span className="ml-1.5 align-middle inline-flex">
                        <SourceBadge kind="manual" />
                      </span>
                    );
                    // System-style hover tooltip (same dark pill as the
                    // ChatHeader hints) showing WHAT the source contains.
                    // The preview string arrives as " | "-separated entries,
                    // each "<head>: <content>" — rendered as one row per
                    // entry with the head (page range / section) highlighted,
                    // instead of a run-on wall of text.
                    // Hover-persistent: the wrapper touches the trigger line
                    // (padding bridge, not a margin gap), so moving the
                    // pointer INTO the tooltip keeps group-hover active and
                    // the text can be selected and copied. invisible (not
                    // pointer-events-none) so the hidden tooltip never
                    // swallows clicks meant for the message above.
                    const sourceTooltip = (text: string) => {
                      const entries = text.split(' | ').map((s) => s.trim()).filter(Boolean);
                      return (
                        <div className="absolute bottom-full left-5 pb-1.5 z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          <div className="relative w-max max-w-[360px] px-3 py-2 rounded-lg bg-[#201e1a] text-[11px] font-normal leading-snug shadow-lg text-left select-text cursor-text">
                            <div className="space-y-1.5">
                              {entries.map((entry, i) => {
                                const sep = entry.indexOf(': ');
                                const head = sep > 0 && sep <= 40 ? entry.slice(0, sep) : '';
                                const rest = head ? entry.slice(sep + 2) : entry;
                                return (
                                  <div key={i}>
                                    {head ? (
                                      <span className="font-semibold text-teal-300">{head} · </span>
                                    ) : null}
                                    <span className="text-white/90">{rest}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="absolute top-full left-4 border-4 border-transparent border-t-[#201e1a]" />
                          </div>
                        </div>
                      );
                    };
                    if (enrichmentSource) {
                      const enrichmentLabel = typeof enrichmentSource.label === 'string' ? enrichmentSource.label : '';
                      const enrichmentPreview = typeof enrichmentSource.preview === 'string' ? enrichmentSource.preview : '';
                      lines.push(
                        <div key="enrichment" className="group relative" style={lineStyle}>
                          <Sparkles style={{ ...lineIcon, color: '#0d9488' }} />
                          <span style={{ wordBreak: 'break-word' }}>
                            <span style={labelStyle}>{t('chat.botMessage.sourceEnrichment', { defaultValue: 'Your reference data' })}</span>
                            {enrichmentLabel ? `: ${enrichmentLabel}` : ''}
                            {verifiedChip}
                          </span>
                          {enrichmentPreview ? sourceTooltip(enrichmentPreview) : null}
                        </div>
                      );
                    }
                    {/* Indexed reference documents (manuals) — org-provided
                        like enrichment, so it follows right after it. */}
                    const referenceDocSource = sources.find((s) => s?.type === 'reference_doc');
                    if (referenceDocSource) {
                      const refDocLabel = typeof referenceDocSource.label === 'string' ? referenceDocSource.label : '';
                      const refDocPreview = typeof referenceDocSource.preview === 'string' ? referenceDocSource.preview : '';
                      const refExcerpts: any[] = Array.isArray(message_data.reference_excerpts)
                        ? message_data.reference_excerpts.filter((e: any) => e && typeof e.text === 'string' && e.text.trim())
                        : [];
                      lines.push(
                        <div key="reference_doc" className="group relative" style={lineStyle}>
                          <BookOpen style={{ ...lineIcon, color: '#0d9488' }} />
                          <span style={{ wordBreak: 'break-word' }}>
                            <span style={labelStyle}>{t('chat.botMessage.sourceReferenceDoc', { defaultValue: 'Your reference documents' })}</span>
                            {refDocLabel ? `: ${refDocLabel}` : ''}
                            {verifiedChip}
                            {refExcerpts.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setShowReferencesModal(true)}
                                className="ml-1.5 text-teal-600 hover:text-teal-800 underline underline-offset-2 font-medium"
                              >
                                {t('chat.botMessage.viewPassages', { defaultValue: 'view passages' })}
                              </button>
                            )}
                          </span>
                          {refDocPreview ? sourceTooltip(refDocPreview) : null}
                        </div>
                      );
                      if (showReferencesModal && refExcerpts.length > 0) {
                        lines.push(createPortal(
                          <div
                            key="reference_modal"
                            onClick={() => setShowReferencesModal(false)}
                            style={{
                              position: 'fixed', inset: 0, zIndex: 99999,
                              backgroundColor: 'rgba(0,0,0,0.4)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              padding: '20px',
                            }}
                          >
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col"
                            >
                              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                                <div className="flex items-center gap-2 min-w-0">
                                  <BookOpen className="w-4 h-4 text-teal-600 flex-shrink-0" />
                                  <span className="text-sm font-semibold text-gray-800 truncate">
                                    {t('chat.botMessage.referencePassagesTitle', { defaultValue: 'Reference passages' })}
                                    {refDocLabel ? ` — ${refDocLabel}` : ''}
                                  </span>
                                  {verifiedChip}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setShowReferencesModal(false)}
                                  className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1"
                                  aria-label={t('chat.botMessage.close', { defaultValue: 'Close' })}
                                >
                                  ×
                                </button>
                              </div>
                              <div className="overflow-y-auto px-5 py-4 space-y-3.5 select-text bg-gray-50/60 rounded-b-2xl">
                                {(() => {
                                  // Raw pypdf text carries hard mid-sentence line
                                  // breaks and inline [p.N] markers — reflow into
                                  // per-page paragraphs with a page badge each.
                                  const pageSegments = (raw: string) => {
                                    const parts = String(raw).split(/\[p\.(\d+)\]\s*/);
                                    const segs: { page: string | null; text: string }[] = [];
                                    if (parts[0] && parts[0].trim()) segs.push({ page: null, text: parts[0] });
                                    for (let k = 1; k < parts.length; k += 2) {
                                      segs.push({ page: parts[k], text: parts[k + 1] || '' });
                                    }
                                    return segs
                                      .map((seg) => ({
                                        ...seg,
                                        text: seg.text.replace(/\s*\n\s*/g, ' ').replace(/\s{2,}/g, ' ').trim(),
                                      }))
                                      .filter((seg) => seg.text);
                                  };
                                  const distinctTitles = new Set(refExcerpts.map((e: any) => e.title)).size;
                                  const copyExcerpt = (e: any, i: number) => {
                                    const plain = pageSegments(e.text)
                                      .map((seg) => (seg.page ? `(p. ${seg.page}) ` : '') + seg.text)
                                      .join('\n\n');
                                    navigator.clipboard?.writeText(
                                      `${e.title} — pp. ${e.page_from}–${e.page_to}\n\n${plain}`,
                                    ).then(() => {
                                      setCopiedExcerptIndex(i);
                                      setTimeout(() => setCopiedExcerptIndex(null), 1500);
                                    }).catch(() => {});
                                  };
                                  return refExcerpts.map((e: any, i: number) => (
                                    <div key={i} className="rounded-xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
                                      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/80">
                                        <div className="flex items-center gap-2 min-w-0 text-[11px]">
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold border border-teal-200/70 whitespace-nowrap">
                                            pp. {e.page_from}–{e.page_to}
                                          </span>
                                          <span className="text-gray-500 capitalize whitespace-nowrap">
                                            {String(e.doc_type || 'other').replace(/_/g, ' ')}
                                          </span>
                                          {distinctTitles > 1 && (
                                            <span className="text-gray-400 truncate">· {e.title}</span>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => copyExcerpt(e, i)}
                                          className="text-[11px] font-medium text-gray-400 hover:text-teal-700 transition-colors whitespace-nowrap"
                                        >
                                          {copiedExcerptIndex === i
                                            ? t('chat.botMessage.copied', { defaultValue: 'Copied ✓' })
                                            : t('chat.botMessage.copy', { defaultValue: 'Copy' })}
                                        </button>
                                      </div>
                                      <div className="px-4 py-3 space-y-2.5">
                                        {pageSegments(e.text).map((seg, j) => (
                                          <div key={j} className="flex gap-2.5 items-baseline">
                                            <span className="flex-shrink-0 text-[10px] font-semibold text-gray-400 tabular-nums w-9 text-right">
                                              {seg.page ? `p. ${seg.page}` : ''}
                                            </span>
                                            <p className="text-[12.5px] leading-relaxed text-gray-700 m-0">
                                              {seg.text}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ));
                                })()}
                              </div>
                            </div>
                          </div>,
                          document.body,
                        ));
                      }
                    }
                    // "Your vehicle" is deliberately NOT listed — it is
                    // already known (order header); older messages carrying a
                    // vehicle source simply skip the line.
                    if (partsSource) {
                      lines.push(
                        <div key="parts" style={lineStyle}>
                          <Package style={{ ...lineIcon, color: '#7c3aed' }} />
                          <span style={{ wordBreak: 'break-word' }}>
                            <span style={labelStyle}>{t('chat.botMessage.sourceParts', { defaultValue: 'Your search results' })}</span>
                            {partsNote ? `: ${partsNote}` : ''}
                          </span>
                        </div>
                      );
                    }
                    {/* Legacy messages stored before the vehicle/parts split. */}
                    if (hasCatalog) {
                      lines.push(
                        <div key="catalog" style={lineStyle}>
                          <Database style={{ ...lineIcon, color: '#2563eb' }} />
                          <span style={labelStyle}>
                            {t('chat.botMessage.sourceCatalog', { defaultValue: 'Catalog & session data' })}
                          </span>
                          <span className="ml-1.5 inline-flex"><SourceBadge kind="epc" /></span>
                        </div>
                      );
                    }
                    // The org-datasource line is deliberately NOT rendered
                    // (not in the design) — the "Found in your data sources"
                    // card already communicates it where results appear.
                    if (webSources.length > 0) {
                      lines.push(
                        <div key="web" style={lineStyle}>
                          <Globe style={{ ...lineIcon, color: '#6b7280' }} />
                          <span style={{ wordBreak: 'break-word' }}>
                            <span style={labelStyle}>{t('chat.botMessage.sourceWeb', { defaultValue: 'Web' })}</span>
                            <span className="mx-1.5 inline-flex align-middle"><SourceBadge kind="internet" /></span>
                            {': '}
                            {webSources.map((s, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && <span aria-hidden="true" style={{ color: '#d1d5db' }}>{' · '}</span>}
                                <a
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={s.url}
                                  style={{ color: '#4b5563', textDecoration: 'underline', textDecorationColor: '#d1d5db', textUnderlineOffset: '2px' }}
                                >
                                  {webLabel(s)}
                                </a>
                              </React.Fragment>
                            ))}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                        {/* Render whenever there is ANYTHING to say about trust —
                            a pure general-AI answer has no source lines but
                            still needs its unverified verdict. */}
                        {(lines.length > 0 || hasGeneral || webSources.length > 0) && (
                          <>
                            <div
                              style={{
                                fontWeight: 600, color: '#9ca3af', fontSize: '10px',
                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                marginBottom: '4px',
                              }}
                            >
                              {t('chat.botMessage.sourcesLabel', { defaultValue: 'This answer is based on' })}
                            </div>
                            {/* ONE overall trust verdict — the per-source lines
                                below are the detail. Without it, a green EPC
                                badge next to an amber Internet badge left the
                                user unable to tell whether to rely on the
                                answer (live 2026-08-24). */}
                            {(() => {
                              const trusted = sources.some((s) =>
                                ['enrichment', 'reference_doc', 'catalog', 'parts', 'org_datasource'].includes(s?.type));
                              const untrusted = hasGeneral || webSources.length > 0;
                              const verdict = trusted && !untrusted ? 'verified' : trusted ? 'mixed' : 'unverified';
                              const cfg = verdict === 'verified'
                                ? {
                                    bg: '#ecfdf5', border: '#a7f3d0', fg: '#047857',
                                    Icon: ShieldCheck,
                                    text: t('chat.botMessage.verdictVerified', {
                                      defaultValue: 'Verified — based on your organisation\u2019s data and documents.',
                                    }),
                                  }
                                : verdict === 'mixed'
                                  ? {
                                      bg: '#fffbeb', border: '#fde68a', fg: '#8a5a00',
                                      Icon: Sparkles,
                                      text: t('chat.botMessage.verdictMixed', {
                                        defaultValue: 'Partially verified — items marked Internet or general AI need double-checking before ordering.',
                                      }),
                                    }
                                  : {
                                      bg: '#fffbeb', border: '#fde68a', fg: '#8a5a00',
                                      Icon: Sparkles,
                                      text: t('chat.botMessage.verdictUnverified', {
                                        defaultValue: 'Unverified — from the web / general AI knowledge; double-check before relying on it.',
                                      }),
                                    };
                              const VIcon = cfg.Icon;
                              return (
                                <div style={{
                                  display: 'flex', alignItems: 'flex-start', gap: '6px',
                                  padding: '6px 10px', marginBottom: '7px',
                                  borderRadius: '10px', backgroundColor: cfg.bg,
                                  border: `1px solid ${cfg.border}`, color: cfg.fg,
                                  fontSize: '11.5px', fontWeight: 600, lineHeight: 1.45,
                                }}>
                                  <VIcon style={{ width: '13px', height: '13px', flexShrink: 0, marginTop: '2px' }} />
                                  <span>{cfg.text}</span>
                                </div>
                              );
                            })()}
                            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '3px' }}>
                              {lines}
                            </div>
                          </>
                        )}
                        {/* The old italic general-AI disclaimer is subsumed by
                            the verdict banner above (mixed/unverified states
                            carry the double-check warning). */}
                      </div>
                    );
                  })()}
                  </>
                )}
                {/* Structured clarification form — rendered below the intro text,
                    inside the bot bubble, when the envelope carries questions. */}
                {hasClarification && !isSupersededClarification && clarificationQuestions && (
                  <StructuredQuestionForm
                    questions={clarificationQuestions}
                    t={t}
                    headerLabels={clarificationHeaderLabels}
                    requestLines={clarificationRequestLines}
                    initialAnswers={clarificationAnswers}
                    onRequestHint={onRequestHint}
                    onChatAboutQuestion={onChatAboutQuestion}
                    onLoadChatHistory={onLoadChatHistory}
                    chatContext={clarificationChatContext}
                    onNewSearch={onSendMessage}
                    onPartEdit={partEdit}
                    previousSteps={previousClarifications}
                    waitingNext={waitingNextQuestion}
                    // Locked once answered and forever after: a clarification is
                    // only answerable while it is the LAST message. As soon as the
                    // user submits (an optimistic answer + response are appended) it
                    // is no longer last, so it stays disabled — including on history
                    // reload, where any message following it means it was answered.
                    disabled={isLoading || !isLastMessage || readOnly}
                    lockedNote={readOnly ? t('chat.clarification.ownerOnly', { defaultValue: 'Only the order owner can answer. Use “Take it” to work on this order.' }) : undefined}
                    onSubmit={(answerMessage, structuredAnswer) => {
                      if (onSubmitClarification) {
                        onSubmitClarification(answerMessage, structuredAnswer);
                      }
                    }}
                  />
                )}
              </div>
            )}

            {/* Inline attachments (images + PDFs) forwarded from an email-intake note */}
            {message.uploadedFiles && message.uploadedFiles.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.uploadedFiles.map((file, index) => {
                  const src = toHttpUrl(file.preview || file.data);
                  if (file.mimeType?.startsWith('image/')) {
                    return (
                      <div key={index} className="relative">
                        <img
                          src={src}
                          alt={file.name || `Image ${index + 1}`}
                          className="max-w-full h-auto rounded-lg shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                          style={{ maxHeight: '200px', width: 'auto', objectFit: 'contain' }}
                          onClick={() => setZoomedSrc(src)}
                        />
                        {file.name && (
                          <div className="mt-1 text-xs text-gray-500 truncate max-w-[200px]">
                            {file.name}
                          </div>
                        )}
                      </div>
                    );
                  }
                  // Non-image (PDF / other) → clickable chip.
                  return (
                    <a
                      key={index}
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={file.name}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                      <span className="truncate max-w-[180px]">{file.name || 'Attachment'}</span>
                    </a>
                  );
                })}
              </div>
            )}

            {/* A turn that failed always offers the way back. Retry used to
                render only for the literal "null" text, so an errored turn left
                the user with a dead apology and nothing to click — and a wrong
                answer is worse than no answer, so the error path has to be
                usable (Ben, 2026-09-21). */}
            {(message_data.text_message === "null" || message_data.hasError) && !isVisionAnalysis && (
              <div className="flex flex-col gap-3">
                {message_data.text_message === "null" && (
                  <div className="text-sm text-gray-600">
                    {t('chat.botMessage.noResponse')}
                  </div>
                )}
                <button
                  onClick={handleRetryFromBot}
                  disabled={isLoading}
                  className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-blue-800 px-4 py-2 rounded-lg transition-colors text-sm font-medium self-start"
                >
                  <RefreshCwIcon className="w-4 h-4" />
                  {t('chat.botMessage.retry')}
                </button>
              </div>
            )}

       
            {postponedQuestions.map(q => (
              <SkippedQuestionCard
                key={`skipped-${q.id}`}
                question={q}
                requestLine={clarificationRequestLines[q.id]}
                t={t}
                answered={!!(q as any).answered}
                disabled={isLoading || readOnly}
                openSignal={postponedOpen[q.id] ?? null}
                // Not gated on being the last message: a skipped question is
                // answered from the result whenever the customer is ready.
                onRequestHint={currentUser ? requestClarificationHint : undefined}
                onChatAboutQuestion={currentUser ? chatAboutQuestion : undefined}
                onLoadChatHistory={currentUser ? loadChatHistory : undefined}
                chatContext={clarificationChatContext}
                onPartEdit={partEdit}
                onSubmit={(answerMessage, structuredAnswer) => {
                  if (onSubmitClarification) onSubmitClarification(answerMessage, structuredAnswer);
                }}
              />
            ))}

            {/* Show multiple parts results or supplier suggestions / not-found items if available */}
            {showsResultsBlock(message, message_data) && (
              <div className="mt-2 card-enter">
                <MultiPartResultView
                  parts_results={message.parts_results || []}
                  messageText={message_data.text_message || ''}
                  messageId={message.id}
                  isValidImageData={isValidImageData}
                  onOrderUpdate={onOrderUpdate}
                  orderUpdateTrigger={orderUpdateTrigger}
                  onSearchPart={onSearchPart}
                  onSendSilent={onSendSilent}
                  onAddToCart={onAddToCart}
                  structuredData={message.structured_data}
                  isLoading={isLoading}
                  requestedParts={message_data.requested_parts}
                  notFoundParts={message_data.not_found_parts}
                  notFoundReasons={(message_data as any).not_found_reasons}
                  supplierSuggestions={message_data.supplier_suggestions}
                  supplierSuggestionsVehicle={(message_data as any).supplier_suggestions_vehicle}
                  cartItems={cartItems}
                  seenPartNumbers={seenPartNumbers}
                  priorParts={priorParts}
                  // The message's own ledger snapshot. The stream carries it as
                  // a frame field; the search envelope repeats it, which is what
                  // a replayed history message has.
                  lines={(message as any).lines ?? (message_data as any).lines}
                  turnLines={(message as any).turn_lines ?? (message_data as any).turn_lines}
                  sessionHasLedger={sessionHasLedger}
                  validationItems={validationItems}
                  isLatestResults={isLatestResults}
                  complementaryPending={complementaryPending}
                  onViewModeChange={setPartsViewMode}
                  postponedQuestionIds={postponedQuestions.map(q => q.id)}
                  onPostponedAction={openPostponed}
                />
              </div>
            )}

            {/* Data-source (vector search) hits on turns with NO catalog results:
                the results table doesn't render, so the suggestions get their
                own block (the with-results case renders them inside the table). */}
            {(!message.parts_results || message.parts_results.length === 0) && (
              <DataSourceSuggestions
                suggestions={message_data.supplier_suggestions}
                vehicle={(message_data as any).supplier_suggestions_vehicle}
                onAddToCart={onAddToCart}
                structuredData={message.structured_data}
                cartItems={cartItems}
              />
            )}

            {/* Persistent post-search summary — last ACK event shown after parts
                load. Transient ones (the instant "Got it — reading your
                request…") are streaming-only: on a turn with no other ACK they
                were pinned here under the next question (live 2026-10-12). */}
            { (() => {
              const ackEvents = (message.events || []).filter(e => e.type === 'ack' && e.content?.trim() && !e.transient);
              if (ackEvents.length === 0) return null;
              const lastAck = ackEvents[ackEvents.length - 1].content.trim();
              return (
                <div
                  dangerouslySetInnerHTML={{ __html: renderMarkdownNoEscape(lastAck) }}
                  style={{
                    marginTop: '6px',
                    fontSize: '13px',
                    color: '#5c5adb',
                    padding: '7px 12px',
                    lineHeight: '1.6',
                    backgroundColor: '#f3f2ff',
                    borderRadius: '10px',
                    border: '1px solid #dddcff',
                  }}
                />
              );
            })()}

            <span className={`text-xs font-jakarta font-medium mt-1 block ${message.isError ? 'text-white/70' : 'text-gray-500'}`}>
              {formatTime(message.timestamp)}
              {/* How long the turn took (send → the moment it was the user's move
                  again). Admins see it on every finished turn; the debug chip
                  below has the breakdown. */}
              {showTurnTook && message.turnTiming && (
                <span className="opacity-70" title="From sending the message until it was your move again">
                  {' · took '}{(message.turnTiming.clientMs / 1000).toFixed(1)}s
                </span>
              )}
            </span>

            {/* Admin + debug mode: how long the turn took until it was the
                human's move again (send → terminal final frame). */}
            {showDebugPanel && message.turnTiming && <TurnClockChip timing={message.turnTiming} />}

            {/* Admin debug panel — probability scores, parts state, raw model output */}
            {showDebugPanel && <MessageDebugPanel message={message} />}

            {/* Message feedback buttons */}
            {/* <MessageFeedback message={message} onFeedback={onFeedback} /> */}
            </div>
            )}

            {/* Marked photo — above the progress bubble. The markings are a
                finding the user acts on; a "still working" notice must never
                sit between them and the analysis text. */}
            {showAnnotatedBlock && hasMainContent && (
              <div className="w-full sm:w-80 lg:w-96 xl:w-[28rem] min-w-0">
                <AnnotatedImagesBlock
                  images={annotated_images}
                  onImageClick={setZoomedImageIndex}
                  details={<VisionPhotoDetails messageTime={message.timestamp} />}
                />
                {/* Admin + debug mode: what vision read and what the search used. */}
                {showDebugPanel && <VisionDebugInline refreshKey={`${message.id}-${message.isStreaming ? 1 : 0}`} messageTime={message.timestamp} />}
              </div>
            )}

            {/* Streaming indicator — rendered below the content bubble so the
                summary/parts list appears first and the "Thinking…" bubble
                sits underneath it while more events are still arriving. */}
            {message.isStreaming && (
              <div className={`p-2 sm:p-3 rounded-2xl card-enter shadow-sm break-words overflow-hidden ${message.isError ? 'bg-red-500 text-white' : 'bg-white text-gray-900'}`}>
                {message.intentPreview && (
                  <div className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 mb-3 border-l-2 border-primary-400">
                    {typedIntentPreview}
                  </div>
                )}
                {hideStreamingSteps ? (
                  <div className="flex items-center gap-2 px-1 py-2">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  </div>
                ) : (
                  <StreamingIndicator message={message} />
                )}
              </div>
            )}
          </div>
        </div>

        {(zoomedSrc || (zoomedImageIndex !== null && annotated_images?.[zoomedImageIndex])) && (
          <ImageZoomModal
            src={zoomedSrc ?? annotated_images[zoomedImageIndex!]}
            alt={zoomedSrc ? 'Attachment (zoomed)' : `Annotated analysis ${(zoomedImageIndex ?? 0) + 1} (zoomed)`}
            onClose={() => { setZoomedImageIndex(null); setZoomedSrc(null); }}
          />
        )}
      </div>}

      {/* Annotated photo for a bubble with no main content of its own — the
          one with content renders it inside its column, above the progress. */}
      {showAnnotatedBlock && !hasMainContent && (
        <div className="flex gap-2 sm:gap-3 flex-row">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center flex-shrink-0 bg-transparent">
            <img src={getVersionedPublicUrl('/32x32/favicon.svg')} alt="Cata" className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="w-full sm:w-80 lg:w-96 xl:w-[28rem] min-w-0">
            <AnnotatedImagesBlock
              images={annotated_images}
              onImageClick={setZoomedImageIndex}
              details={<VisionPhotoDetails messageTime={message.timestamp} />}
            />
            {showDebugPanel && <VisionDebugInline refreshKey={`${message.id}-${message.isStreaming ? 1 : 0}`} messageTime={message.timestamp} />}
          </div>
        </div>
      )}

      {/* SyncAgent result — rendered as its own separate bubble below the streaming indicator */}
      {message_data.syncAgent && isSyncAgentResponseShouldDisplay(message_data.syncAgent) && (
        <div className="flex gap-2 sm:gap-3 flex-row">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center flex-shrink-0 bg-transparent">
            <img src={getVersionedPublicUrl('/32x32/favicon.svg')} alt="Cata" className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="w-full sm:w-80 lg:w-96 xl:w-[28rem] p-2 sm:p-3 rounded-2xl shadow-sm break-words overflow-hidden bg-white text-gray-900">
            <SyncAgentResultView
              response={message_data.syncAgent}
              annotatedImages={annotated_images}
              onImageClick={setZoomedImageIndex}
            />
          </div>
        </div>
      )}

      {/* Direct Search - shown as follow-up */}
      {message.structured_data && message.structured_data?.part_data && (
        <div className="flex gap-2 sm:gap-3 flex-row">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center flex-shrink-0 bg-transparent">
            <img src={getVersionedPublicUrl('/32x32/favicon.svg')} alt="Cata" className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="w-full sm:w-80 lg:w-96 xl:w-[28rem] p-2 sm:p-3 rounded-2xl shadow-sm break-words overflow-hidden bg-white text-gray-900">
            {t('chat.directSearch.nextPrompt', { defaultValue: 'What would you like to search next?' })}
          </div>
        </div>
      )}

      {/* Follow-up CTA — the search agent's forward-looking sentence under the
          LAST completed search / clarification-answer turn. It names the
          related parts for this repair and invites the user to continue the
          search by replying. Chips are intentionally NOT rendered (product
          call, 2026-08-06) — the sentence is the whole CTA; the chip decision
          layer (utils/suggestedActions.ts) is kept tested for easy re-enable.
          Hidden while a clarification question awaits an answer (the form is
          the call to action there) and on older messages. */}
      {isLastMessage && !isLoading && !message.isStreaming && !hasClarificationQuestions && (() => {
        // Preferred: structured pills that each carry their own first-person
        // request + action ("qa"/"search"). The tap self-describes, so a QA pill
        // ("…replacement instructions…?") deterministically routes to Q&A instead
        // of the backend having to match the pill text against a session map
        // (that lookup missed after reload/translation and re-ran the search).
        const ctaActions = ((message as any).cta_actions || (message_data as any)?.cta_actions) as
          | Array<{ text?: string; request?: string; action?: string }>
          | undefined;
        const cta = String(
          (message as any).callToAction || (message_data as any)?.callToAction || '',
        ).trim();

        // Clicking a pill accepts that suggestion: silent-send (no user echo
        // bubble — same convention as the refine popup) so it runs immediately.
        const sendMarker = (marker: string, fallbackText: string) => () => {
          if (readOnly) { promptTakeOrder?.(); return; }
          if (onSendSilent) onSendSilent(marker);
          else onSearchPart?.(fallbackText);
        };

        let prompts: Array<{ text: string; onClick: () => void }> = [];
        if (Array.isArray(ctaActions) && ctaActions.length) {
          prompts = ctaActions
            .filter((a) => (a?.text || '').trim() && (a?.request || '').trim())
            .map((a) => {
              const action = a.action === 'qa' ? 'qa' : 'search';
              const request = String(a.request).trim();
              const marker = `SYSTEM_MESSAGE:CTA_ACCEPT|action=${action}|request=${request}`;
              return { text: String(a.text).trim(), onClick: sendMarker(marker, request) };
            });
        } else if (cta) {
          // Legacy fallback: the backend shipped only the newline-joined
          // questions. Echo the question in the old acceptance marker (resolved
          // server-side via the session map).
          prompts = cta
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({
              text: line,
              onClick: sendMarker(
                `SYSTEM_MESSAGE:user accepted the assistant's follow-up suggestion — proceed with it: "${line}"`,
                line,
              ),
            }));
        }
        if (!prompts.length) return null;
        return <SuggestedActions actions={[]} prompts={prompts} />;
      })()}
    </>
  );
});

export default BotMessage;
